/* eslint-disable @typescript-eslint/restrict-plus-operands */
import {
  ISmartCanvasClickedItemEvent,
  ISmartCanvasLayer,
  ISmartCanvasOptions,
  ISmartCanvasSelectionArea,
} from 'src/modules/shared/models/interfaces/smart-canvas.interface';
import { IPageDimensions } from 'src/modules/shared/models/interfaces/page-dimensions.interface';
import { Polygon } from 'src/modules/shared/models/robots.model';
import { Calcs } from 'src/modules/shared/utilities/calculations';
import { EventEmitterService } from '../../../shared/services/event-emitter.service';
import { Component, Input, OnInit } from '@angular/core';
import { IFilePageAndWords } from 'src/modules/shared/models/interfaces/files.interface';

declare const Konva: any;

@Component({
  selector: 'hcr-smart-canvas-selection-area',
  template: '',
})
export class SmartCanvasSelectionAreaComponent implements OnInit {
  @Input() selectedFilePagesAndWords: IFilePageAndWords[];
  @Input() smartCanvasOptions: ISmartCanvasOptions;
  @Input() stagePageSizes: IPageDimensions[];
  @Input() konvaStage: any; //Konva.Stage;
  @Input() layer: ISmartCanvasLayer;
  @Input() layersToSelect: ISmartCanvasLayer[];
  @Input() states: { [key: string]: boolean };

  public activeSelectionArea: ISmartCanvasSelectionArea;
  private _posStart: { x: number; y: number };
  private _posNow: { x: number; y: number };

  private _selectionRectangle = new Konva.Rect({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    strokeWidth: 1,
    stroke: '#d5d5d5',
    fill: '#8080801c',
    opacity: 1,
    dash: [2, 2],
  });

  private _reverse(r1: { x: number; y: number }, r2: { x: number; y: number }) {
    let r1x = r1.x,
      r1y = r1.y,
      r2x = r2.x,
      r2y = r2.y,
      d: number;

    if (r1x > r2x) {
      d = Math.abs(r1x - r2x);
      r1x = r2x;
      r2x = r1x + d;
    }

    if (r1y > r2y) {
      d = Math.abs(r1y - r2y);
      r1y = r2y;
      r2y = r1y + d;
    }

    return { x1: r1x, y1: r1y, x2: r2x, y2: r2y };
  }

  private _hide(): void {
    this._selectionRectangle.hide();
    this.activeSelectionArea = null;
    this._eventEmitterService.get('hide-group-key-selection').emit();
  }

  constructor(private _eventEmitterService: EventEmitterService) {}

  ngOnInit(): void {
    this.layer.konvaLayer.add(this._selectionRectangle);

    this._eventEmitterService.get('hide-selection-area').subscribe(() => {
      this._hide();
    });

    this.konvaStage.off('mousedown.canvas-selection').on('mousedown.canvas-selection', (event) => {
      if (!this.smartCanvasOptions.enableAreaSelection || event.evt.button !== 0) {
        return;
      }

      this.activeSelectionArea = null;
      this._posStart = this.konvaStage.getRelativePointerPosition();
      this._selectionRectangle.x(this._posStart.x);
      this._selectionRectangle.y(this._posStart.y);
      this._selectionRectangle.width(0);
      this._selectionRectangle.height(0);
      this._hide();

      this.konvaStage.off('mousemove.selection').on('mousemove.selection', () => {
        this._posNow = this.konvaStage.getRelativePointerPosition();

        const posRect = this._reverse(this._posStart, this._posNow);

        this._selectionRectangle.x(posRect.x1);
        this._selectionRectangle.y(posRect.y1);
        this._selectionRectangle.width(posRect.x2 - posRect.x1);
        this._selectionRectangle.height(posRect.y2 - posRect.y1);
        this._selectionRectangle.visible(true);

        this.konvaStage.container().style.cursor = 'crosshair';
      });

      this.konvaStage.off('mouseup.selection').on('mouseup.selection', (opt) => {
        this.konvaStage.off('mousemove.selection');
        this.konvaStage.off('mouseup.selection');
        this.konvaStage.container().style.cursor = 'default';

        const points = [
          <number>this._selectionRectangle.x(),
          <number>this._selectionRectangle.y(),
          <number>this._selectionRectangle.x() + <number>this._selectionRectangle.width(),
          <number>this._selectionRectangle.y(),
          <number>this._selectionRectangle.x() + <number>this._selectionRectangle.width(),
          <number>this._selectionRectangle.y() + <number>this._selectionRectangle.height(),
          <number>this._selectionRectangle.x(),
          <number>this._selectionRectangle.y() + <number>this._selectionRectangle.height(),
        ];

        const wordsInArea = Calcs.getPolygonsFromCanvasArea(
          Calcs.absoluteToRelativePoints(
            points,
            this.stagePageSizes,
            <number>opt.target.attrs.pageIndex,
            this.smartCanvasOptions.pageGutter
          ),
          <number>opt.target.attrs.pageIndex,
          this.layersToSelect[0]
        ).filter((word) => {
          if (this.states.splitWords) {
            return word.content['words'] ? false : word;
          } else {
            return word.content['words'] ? word : false;
          }
        });

        if (wordsInArea.length) {
          const poly = new Polygon(Calcs.boundingBoxPixelsToRelative(points, this.stagePageSizes[wordsInArea[0].pageIndex]));

          this.activeSelectionArea = {
            boundingBox: poly.boundingBox,
            pageNumber: wordsInArea[0].pageNumber,
            pageIndex: wordsInArea[0].pageIndex,
            polygonsInside: wordsInArea,
            centroid: poly.centroid,
            fixedTooltipPosition: true,
            tooltipPosition: {
              x: opt.evt.offsetX - opt.currentTarget.getStage().content.offsetLeft,
              y: opt.evt.offsetY - opt.currentTarget.getStage().content.offsetTop,
            },
          };

          if (this.smartCanvasOptions.showGroupKeys) {
            this._eventEmitterService.get<ISmartCanvasSelectionArea>('show-group-key-selection').emit(this.activeSelectionArea);
          } else {
            this._eventEmitterService
              .get<ISmartCanvasClickedItemEvent>('clicked-value')
              .emit({ groupKey: null, selectedArea: this.activeSelectionArea });

            this._hide();
          }
        } else {
          this._hide();
        }
      });
    });
  }
}
